/**
 * Application header.
 *
 * Below 768 px (APP1 mockup): app icon + "Lukaaji" + a profile icon; the bottom nav carries the
 * navigation and the language select lives in Profil. From 768 px: desktop links, the language
 * menu and the account menu (or login / get started).
 */
import { Link, useNavigate } from 'react-router-dom';
import { User, LogOut, Settings, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect, useId, useCallback } from 'react';
import { Button } from '@/components/ui';
import { useAuthStore, useLanguage, AVAILABLE_LANGUAGES, useSetLanguage, useClosePlayer } from '@/store';
import { getBottomNavTranslation, getHeaderTranslation } from '@/utils';
import type { BottomNavTranslationKey, HeaderTranslationKey } from '@/utils/translations';
import { isAcousticEnabled } from '@/config/features';
import { getNavItems, type NavItemId } from '@/config/navigation';
import { ROUTES } from '@/config/routes';

/** Desktop header links: nav item → label (the full session names have room here). */
const DESKTOP_LINKS: { id: NavItemId; labelKey: BottomNavTranslationKey }[] = [
  { id: 'home', labelKey: 'home' },
  { id: 'acoustic', labelKey: 'acousticSessions' },
  { id: 'guided', labelKey: 'guidedSessions' },
  { id: 'subscription', labelKey: 'subscription' },
];

/** Closes a popup when the user clicks outside it or presses Escape. */
function useDismiss(isOpen: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    function onPointerDown(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) close();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, close]);
  return ref;
}

export function Header() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();
  const language = useLanguage();
  const setLanguage = useSetLanguage();
  const closePlayer = useClosePlayer();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const closeLanguageMenu = useCallback(() => setIsLanguageMenuOpen(false), []);
  const closeUserMenu = useCallback(() => setIsUserMenuOpen(false), []);
  const languageMenuRef = useDismiss(isLanguageMenuOpen, closeLanguageMenu);
  const userMenuRef = useDismiss(isUserMenuOpen, closeUserMenu);
  const languageMenuId = useId();
  const userMenuId = useId();

  const currentLanguage = AVAILABLE_LANGUAGES.find((l) => l.code === language);
  const t = (key: HeaderTranslationKey) => getHeaderTranslation(key, language);
  const nav = (key: BottomNavTranslationKey) => getBottomNavTranslation(key, language);

  const handleLogout = () => {
    closePlayer(); // Stop any playing audio
    logout();
    navigate(ROUTES.home);
    setIsUserMenuOpen(false);
  };

  const acoustic = isAcousticEnabled();
  const navItems = getNavItems({ acoustic });
  // With Akustične and a signed-in user's links, the pre-A4 spacing overflowed 768 px. Only the
  // flag-on build is compacted (names show again from 1024 px); the flag-off header is unchanged.
  const navClass = acoustic ? 'gap-4 lg:gap-6' : 'space-x-6';
  const accountClass = acoustic ? 'gap-2 lg:gap-4' : 'space-x-4';
  const compactLabelClass = acoustic ? 'hidden lg:inline' : undefined;
  const navLinks = DESKTOP_LINKS.flatMap(({ id, labelKey }) => {
    const item = navItems.find((navItem) => navItem.id === id);
    return item ? [{ href: item.path, label: nav(labelKey) }] : [];
  });

  const authNavLinks = [
    { href: ROUTES.favorites, label: nav('favorites') },
    { href: ROUTES.history, label: t('history') },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 sm:h-16 items-center justify-between">
        {/* Logo: app icon + wordmark */}
        <Link
          to={ROUTES.home}
          className="flex min-h-11 items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
        >
          <img
            src="/assets/brand/app-icon-64.png"
            alt=""
            aria-hidden="true"
            width={32}
            height={32}
            className="h-8 w-8 rounded-lg pointer-events-none"
          />
          <span className="text-lg sm:text-xl font-bold text-foreground pointer-events-none">Lukaaji</span>
        </Link>

        {/* Desktop navigation */}
        <nav className={`hidden md:flex items-center ${navClass}`}>
          {[...navLinks, ...(isAuthenticated ? authNavLinks : [])].map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Mobile: profile icon */}
        <Link
          to={ROUTES.profile}
          aria-label={t('profile')}
          className="md:hidden flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <User className="h-6 w-6" aria-hidden="true" />
        </Link>

        {/* Desktop: language + account */}
        <div className={`hidden md:flex items-center ${accountClass}`}>
          <div className="relative" ref={languageMenuRef}>
            <button
              type="button"
              onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
              aria-label={`${t('language')}: ${currentLanguage?.nativeName ?? ''}`}
              aria-expanded={isLanguageMenuOpen}
              aria-controls={languageMenuId}
              className="flex min-h-11 items-center gap-1.5 px-3 text-sm rounded-md border bg-background hover:bg-accent transition-colors"
            >
              <span className="text-base" aria-hidden="true">{currentLanguage?.flag}</span>
              {/* The name is also in the aria-label. */}
              <span className={compactLabelClass}>{currentLanguage?.nativeName}</span>
              <ChevronDown className="h-3 w-3" aria-hidden="true" />
            </button>

            {isLanguageMenuOpen && (
              <div
                id={languageMenuId}
                className="absolute right-0 mt-2 w-48 rounded-md border bg-popover shadow-lg z-50 max-h-80 overflow-y-auto"
              >
                <div className="p-1">
                  {AVAILABLE_LANGUAGES.map((lang) => (
                    <button
                      type="button"
                      key={lang.code}
                      aria-current={language === lang.code ? 'true' : undefined}
                      onClick={() => {
                        setLanguage(lang.code);
                        setIsLanguageMenuOpen(false);
                      }}
                      className={`flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md transition-colors ${
                        language === lang.code
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-accent'
                      }`}
                    >
                      <span className="text-base" aria-hidden="true">{lang.flag}</span>
                      <span>{lang.nativeName}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {isAuthenticated ? (
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                aria-label={user?.name || t('profile')}
                aria-expanded={isUserMenuOpen}
                aria-controls={userMenuId}
                className="flex min-h-11 items-center space-x-2 text-sm font-medium hover:text-foreground"
              >
                <User className="h-5 w-5" aria-hidden="true" />
                <span className={compactLabelClass}>{user?.name}</span>
              </button>

              {isUserMenuOpen && (
                <div id={userMenuId} className="absolute right-0 mt-2 w-48 rounded-md border bg-popover shadow-lg">
                  <div className="p-2">
                    <Link
                      to={ROUTES.profile}
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center space-x-2 rounded-md px-3 py-2 text-sm hover:bg-accent"
                    >
                      <Settings className="h-4 w-4" aria-hidden="true" />
                      <span>{t('profile')}</span>
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center space-x-2 rounded-md px-3 py-2 text-sm hover:bg-accent text-destructive"
                    >
                      <LogOut className="h-4 w-4" aria-hidden="true" />
                      <span>{t('logout')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Button variant="ghost" onClick={() => navigate(ROUTES.login)}>
                {t('login')}
              </Button>
              <Button onClick={() => navigate(ROUTES.register)}>
                {t('getStarted')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
